import axios from 'axios';

const API_BASE_URL = 'https://dev.weloop.tech/api/next';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('authToken');
    console.log('🔑 Request interceptor - Token found:', token);
    console.log('🌐 Making request to:', config.method?.toUpperCase(), config.url);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Token added to request headers');
    } else {
      console.log('⚠️ No token found for request');
    }
    
    return config;
  },
  (error) => {
    console.log('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log('✅ Response received:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    console.log('❌ Response error:', error.response?.status, error.config?.url, error.message);
    
    if (error.response?.status === 401) {
      console.log('🚫 Unauthorized - clearing auth data');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (email, password) => {
    console.log('🔐 Login API call:', { email, password: '***' });
    const response = await api.post('/auth/login', {
      email,
      password,
    });
    console.log('✅ Login response:', response.data);
    return response.data;
  },

  register: async (userData) => {
    console.log('📝 Register API call:', { ...userData, password: '***' });
    const response = await api.post('/auth/register', userData);
    console.log('✅ Register response:', response.data);
    return response.data;
  },

  logout: async () => {
    console.log('🚪 Logout API call');
    const response = await api.post('/auth/logout');
    console.log('✅ Logout response:', response.data);
    return response.data;
  },

  getProfile: async () => {
    console.log('👤 Get Profile API call');
    const response = await api.get('/auth/profile');
    console.log('✅ Get Profile response:', response.data);
    return response.data;
  },
};

export const postsAPI = {
  getPosts: async (page = 1, limit = 10, type = 'foryou', lastPostId = null, coordinates = null) => {
    console.log('📱 Get Posts API call:', { page, limit, type, lastPostId, coordinates });
    let url = `/feed/get-posts?type=${type}&limit=${limit}`;
    
    if (lastPostId) {
      url += `&last_post_id=${lastPostId}`;
    } else {
      url += `&page=${page}`;
    }
    
    // Add coordinates for nearby feed
    if (type === 'nearby' && coordinates && coordinates.latitude && coordinates.longitude) {
      url += `&latitude=${coordinates.latitude}&longitude=${coordinates.longitude}`;
      console.log('📍 Added coordinates to API call:', coordinates);
    }
    
    const response = await api.get(url);
    console.log('✅ Get Posts response:', response.data);
    return response.data;
  },

  getPost: async (postId) => {
    console.log('📄 Get Post API call:', { postId });
    const response = await api.get(`/feed/get-post/${postId}`);
    console.log('✅ Get Post response:', response.data);
    return response.data;
  },

  createPost: async (postData) => {
    console.log('✏️ Create Post API call:', postData);
    const response = await api.post('/feed/create-post', postData);
    console.log('✅ Create Post response:', response.data);
    return response.data;
  },

  updatePost: async (postId, postData) => {
    console.log('🔄 Update Post API call:', { postId, postData });
    const response = await api.put(`/feed/update-post/${postId}`, postData);
    console.log('✅ Update Post response:', response.data);
    return response.data;
  },

  deletePost: async (postId) => {
    console.log('🗑️ Delete Post API call:', { postId });
    const response = await api.delete(`/feed/delete-post/${postId}`);
    console.log('✅ Delete Post response:', response.data);
    return response.data;
  },

  likePost: async (postId) => {
    console.log('❤️ Like Post API call:', { postId });
    const response = await api.post(`/feed/like-post/${postId}`);
    console.log('✅ Like Post response:', response.data);
    return response.data;
  },

  unlikePost: async (postId) => {
    console.log('💔 Unlike Post API call:', { postId });
    const response = await api.delete(`/feed/unlike-post/${postId}`);
    console.log('✅ Unlike Post response:', response.data);
    return response.data;
  },

  addComment: async (postId, comment) => {
    console.log('💬 Add Comment API call:', { postId, comment });
    const response = await api.post(`/feed/add-comment/${postId}`, {
      content: comment,
    });
    console.log('✅ Add Comment response:', response.data);
    return response.data;
  },

  getComments: async (postId, page = 1, limit = 10) => {
    console.log('💬 Get Comments API call:', { postId, page, limit });
    const response = await api.get(`/feed/get-comments/${postId}?page=${page}&limit=${limit}`);
    console.log('✅ Get Comments response:', response.data);
    return response.data;
  },
};

export const usersAPI = {
  getUsers: async (page = 1, limit = 10) => {
    console.log('👥 Get Users API call:', { page, limit });
    const response = await api.get(`/users?page=${page}&limit=${limit}`);
    console.log('✅ Get Users response:', response.data);
    return response.data;
  },

  getUser: async (userId) => {
    console.log('👤 Get User API call:', { userId });
    const response = await api.get(`/users/${userId}`);
    console.log('✅ Get User response:', response.data);
    return response.data;
  },

  updateProfile: async (userData) => {
    console.log('🔄 Update Profile API call:', userData);
    const response = await api.put('/users/profile', userData);
    console.log('✅ Update Profile response:', response.data);
    return response.data;
  },

  followUser: async (userId) => {
    console.log('➕ Follow User API call:', { userId });
    const response = await api.post(`/users/${userId}/follow`);
    console.log('✅ Follow User response:', response.data);
    return response.data;
  },

  unfollowUser: async (userId) => {
    console.log('➖ Unfollow User API call:', { userId });
    const response = await api.delete(`/users/${userId}/follow`);
    console.log('✅ Unfollow User response:', response.data);
    return response.data;
  },

  getFollowers: async (userId, page = 1, limit = 10) => {
    console.log('👥 Get Followers API call:', { userId, page, limit });
    const response = await api.get(`/users/${userId}/followers?page=${page}&limit=${limit}`);
    console.log('✅ Get Followers response:', response.data);
    return response.data;
  },

  getFollowing: async (userId, page = 1, limit = 10) => {
    console.log('👥 Get Following API call:', { userId, page, limit });
    const response = await api.get(`/users/${userId}/following?page=${page}&limit=${limit}`);
    console.log('✅ Get Following response:', response.data);
    return response.data;
  },
};

export const notificationsAPI = {
  getNotifications: async (page = 1, limit = 20) => {
    console.log('🔔 Get Notifications API call:', { page, limit });
    const response = await api.get(`/usercontent/get-notifications?page=${page}&limit=${limit}`);
    console.log('✅ Get Notifications response:', response.data);
    return response.data;
  },

  markAsRead: async (notificationId) => {
    console.log('✅ Mark Notification as Read API call:', { notificationId });
    const response = await api.put(`/usercontent/mark-notification-read/${notificationId}`);
    console.log('✅ Mark as Read response:', response.data);
    return response.data;
  },

  markAllAsRead: async () => {
    console.log('✅ Mark All Notifications as Read API call');
    const response = await api.put('/usercontent/mark-all-notifications-read');
    console.log('✅ Mark All as Read response:', response.data);
    return response.data;
  },

  deleteNotification: async (notificationId) => {
    console.log('🗑️ Delete Notification API call:', { notificationId });
    const response = await api.delete(`/usercontent/delete-notification/${notificationId}`);
    console.log('✅ Delete Notification response:', response.data);
    return response.data;
  },
};

export const chatAPI = {
  getConversations: async (type = 'inbox') => {
    console.log('💬 Get Conversations API call:', { type });
    const response = await api.get(`/chat/get-conversations?type=${type}`);
    console.log('✅ Get Conversations response:', response.data);
    return response.data;
  },

  getMessages: async (conversationId, page = 1, limit = 50) => {
    console.log('💬 Get Messages API call:', { conversationId, page, limit });
    const response = await api.get(`/chat/get-messages?conversation_id=${conversationId}&page=${page}&limit=${limit}`);
    console.log('✅ Get Messages response:', response.data);
    return response.data;
  },

  sendMessage: async (conversationId, message) => {
    console.log('💬 Send Message API call:', { conversationId, message });
    const response = await api.post('/chat/create-message', {
      conversation_id: conversationId,
      message,
    });
    console.log('✅ Send Message response:', response.data);
    return response.data;
  },

  createConversation: async (userId) => {
    console.log('💬 Create Conversation API call:', { userId });
    const response = await api.post('/chat/create-conversation', {
      user_id: userId,
    });
    console.log('✅ Create Conversation response:', response.data);
    return response.data;
  },

  deleteConversation: async (conversationId) => {
    console.log('🗑️ Delete Conversation API call:', { conversationId });
    const response = await api.delete(`/chat/delete-conversation?conversation_id=${conversationId}`);
    console.log('✅ Delete Conversation response:', response.data);
    return response.data;
  },
};

export const searchAPI = {
  search: async (query, type = 'users') => {
    console.log('🔍 Search API call:', { query, type });
    const response = await api.get(`/search?q=${encodeURIComponent(query)}&type=${type}`);
    console.log('✅ Search response:', response.data);
    return response.data;
  },
};

export default api; 